import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Check, Loader2, Trash2, UserCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_LABELS = { lunes: 'Lun', martes: 'Mar', miercoles: 'Mie', jueves: 'Jue', viernes: 'Vie', sabado: 'Sab', domingo: 'Dom' };
const DAY_COLORS = { lunes: 'from-blue-500 to-sky-400', martes: 'from-violet-500 to-purple-400', miercoles: 'from-emerald-500 to-teal-400', jueves: 'from-amber-500 to-yellow-400', viernes: 'from-red-500 to-rose-400', sabado: 'from-pink-500 to-rose-400', domingo: 'from-orange-500 to-amber-400' };

const resolveImg = (url) => {
  if (!url) return null;
  return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

const SavedDietsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterType = searchParams.get('filter');
  const basePath = `/${user?.user_type || 'client'}`;
  const [diets, setDiets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchDiets = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/diet/custom-list`).then(res => setDiets(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDiets(); }, [fetchDiets]);

  const handleDelete = async (dietId) => {
    setDeletingId(dietId);
    try {
      await axios.delete(`${API}/diet/custom/${dietId}`);
      toast.success('Dieta eliminada correctamente');
      setDiets(prev => prev.filter(d => d.id !== dietId));
    } catch {
      toast.error('Error al eliminar la dieta');
    }
    setDeletingId(null);
    setConfirmDeleteId(null);
  };

  const handleActivate = async (dietId) => {
    setActivating(dietId);
    try {
      await axios.post(`${API}/diet/custom/${dietId}/activate`);
      toast.success('Dieta activada correctamente');
      navigate(`${basePath}/diet`);
    } catch {
      toast.error('Error al activar la dieta');
    }
    setActivating(null);
  };

  const getPreviewImage = (diet) => {
    const days = diet.days || {};
    for (const dayData of Object.values(days)) {
      for (const [key, val] of Object.entries(dayData)) {
        if (key === 'bebidas') continue;
        const platos = val?.platos || [];
        if (platos.length > 0 && platos[0].image_url) return resolveImg(platos[0].image_url);
      }
    }
    return null;
  };

  const pageTitle = filterType === 'trainer' ? 'Plan de tu Entrenador' : 'Dietas Guardadas';
  const filteredDiets = filterType === 'trainer'
    ? diets.filter(d => d.type === 'trainer')
    : diets;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#467322]" size={32} /></div>;

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="saved-diets-page">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(`${basePath}/diet`)} className="p-2 rounded-xl hover:bg-gray-100 transition-colors" data-testid="saved-back">
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex items-center gap-2">
          {filterType === 'trainer' && <UserCheck size={20} className="text-emerald-600" />}
          <h1 className="text-xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{pageTitle}</h1>
        </div>
      </div>

      {filteredDiets.length === 0 ? (
        <div className="text-center py-20">
          <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="font-bold text-gray-500 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {filterType === 'trainer' ? 'Tu entrenador aun no te ha asignado una dieta' : 'No tienes dietas guardadas'}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {filterType === 'trainer' ? 'Cuando tu nutricionista cree una dieta para ti, aparecera aqui' : 'Usa el configurador para crear tu primera dieta'}
          </p>
          {!filterType && (
            <button onClick={() => navigate(`${basePath}/diet/configurator`)}
              className="mt-6 px-6 py-3 bg-[#467322] text-white rounded-xl font-bold text-sm hover:bg-[#3A601C] transition-colors" data-testid="go-configurator">
              Crear Dieta
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDiets.map(diet => {
            const previewImg = getPreviewImage(diet);
            const dayCount = (diet.selected_days || []).length;
            const mealCount = (diet.selected_meals || []).length;
            return (
              <div key={diet.id}
                className={`bg-white rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all ${diet.active ? 'border-[#467322] ring-2 ring-[#467322]/20' : 'border-gray-100'}`}
                data-testid={`saved-diet-${diet.id}`}>
                <div className="flex">
                  {/* Preview image */}
                  <div className="w-28 sm:w-36 bg-gray-100 flex-shrink-0 relative overflow-hidden">
                    {previewImg ? (
                      <img src={previewImg} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300"><Calendar size={32} /></div>
                    )}
                    {diet.active && (
                      <div className="absolute top-2 left-2 bg-[#467322] text-white text-[10px] font-bold px-2 py-1 rounded-lg shadow-lg">ACTIVA</div>
                    )}
                    {diet.type === 'trainer' && (
                      <div className="absolute bottom-2 left-2 bg-emerald-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-lg shadow-lg flex items-center gap-1">
                        <UserCheck size={10} /> Entrenador
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4">
                    <p className="font-black text-lg text-[#1A2E10] leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{diet.name}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-gray-400">{dayCount} dias</span>
                      <span className="text-xs text-gray-300">|</span>
                      <span className="text-xs text-gray-400">{mealCount} comidas/dia</span>
                    </div>
                    {/* Day pills */}
                    <div className="flex gap-1 mt-3 flex-wrap">
                      {(diet.selected_days || []).map(dayId => (
                        <span key={dayId} className={`text-[10px] font-bold text-white px-2 py-0.5 rounded-md bg-gradient-to-r ${DAY_COLORS[dayId] || 'from-gray-400 to-gray-500'}`}>
                          {DAY_LABELS[dayId] || dayId}
                        </span>
                      ))}
                    </div>
                    {/* Actions */}
                    <div className="flex gap-2 mt-4">
                      {!diet.active && (
                        <button onClick={() => handleActivate(diet.id)} disabled={activating === diet.id}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[#467322] text-white text-sm font-bold hover:bg-[#3A601C] transition-colors disabled:opacity-50"
                          data-testid={`activate-diet-${diet.id}`}>
                          {activating === diet.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          Activar
                        </button>
                      )}
                      {diet.active && (
                        <div className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-50 text-emerald-700 text-sm font-bold border border-emerald-200">
                          <Check size={14} /> Dieta Activa
                        </div>
                      )}
                      <button onClick={() => setConfirmDeleteId(diet.id)}
                        disabled={deletingId === diet.id}
                        className="flex items-center justify-center w-10 h-10 rounded-xl border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
                        data-testid={`delete-diet-${diet.id}`}>
                        {deletingId === diet.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-fade-in">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={20} className="text-red-500" />
            </div>
            <h3 className="text-lg font-black text-center text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Eliminar Dieta</h3>
            <p className="text-sm text-gray-500 text-center mt-2">Esta accion no se puede deshacer. Se eliminara permanentemente esta dieta guardada.</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirmDeleteId(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition-colors"
                data-testid="delete-cancel-btn">
                Cancelar
              </button>
              <button onClick={() => handleDelete(confirmDeleteId)}
                disabled={deletingId === confirmDeleteId}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-600 transition-colors disabled:opacity-50"
                data-testid="delete-confirm-btn">
                {deletingId === confirmDeleteId ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedDietsPage;
